import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import Avatar from '@mui/material/Avatar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import GetApp from '@mui/icons-material/GetApp';
import ViewColumn from '@mui/icons-material/ViewColumn';
import DragIndicator from '@mui/icons-material/DragIndicator';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Close from '@mui/icons-material/Close';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import FirstPage from '@mui/icons-material/FirstPage';
import LastPage from '@mui/icons-material/LastPage';
import NavigateBefore from '@mui/icons-material/NavigateBefore';
import NavigateNext from '@mui/icons-material/NavigateNext';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Call, CadListStatus, CallSearchRequest, PagedResult, MassUpdateRequest, MassDeleteRequest } from '../../types/Call';
import { CallService } from '../../services/callService';
import { formatDuration, formatIsoDuration } from '../../utils/timeUtils';
import { visuallyHidden } from '@mui/utils';

interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  width?: string;
  piiRestricted?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatter?: (value: any, call: Call) => React.ReactNode;
}

interface ExportData {
  format: 'csv' | 'excel';
  visibleColumns: { key: string; label: string }[];
  currentData: Call[];
}

interface EnhancedCallsDataTableProps {
  data: PagedResult<Call> | null;
  loading: boolean;
  searchRequest: CallSearchRequest;
  onSearchRequestChange: (request: CallSearchRequest) => void;
  onExport: (exportData: ExportData) => void;
  userRole?: string;
  onMassUpdateComplete?: () => void;
  /** When true, injects a match score column showing semantic similarity */
  semanticMode?: boolean;
}

// --- Module-level constants and pure helpers (no component state dependency) ---

const CHIP_SX = { height: 20, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } } as const;
const DATE_FORMAT: Intl.DateTimeFormatOptions = { month: '2-digit', day: '2-digit', year: 'numeric' };
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;

/** How stale the retention floor may get before a selection change re-reads it. */
const POLICY_MAX_AGE_MS = 5 * 60 * 1000;
const FOR_REVIEW = 'For Review';

const matchScoreColumn: ColumnConfig = {
  key: 'matchScore',
  label: '',
  visible: true,
  sortable: false,
  width: '52px',
};

function formatColumnLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}


function getStatusColor(status: string): 'success' | 'error' | 'warning' | 'default' | 'info' {
  switch (status.toLowerCase()) {
    case 'completed':
      return 'success';
    case 'failed':
    case 'ai_failed':
      return 'error';
    case 'processing':
    case 'ai_retry_pending':
      return 'warning';
    case 'uploaded':
    case 'queued_transcription':
      return 'info';
    default:
      return 'default';
  }
}

function getStatusLabel(status: string): string {
  if (!status) return 'Unknown';
  switch (status.toLowerCase()) {
    case 'uploaded': return 'Uploaded';
    case 'queued_transcription': return 'Queued';
    case 'processing': return 'Processing';
    case 'completed': return 'Completed';
    case 'failed': return 'Failed';
    case 'ai_retry_pending': return 'AI Retry Pending';
    case 'ai_failed': return 'AI Failed';
    default: return status;
  }
}

/**
 * The CAD column. Every state has an icon and a word as well as a color, and Missing is the only
 * filled chip, so the calls that need a report are the ones that stand out in a page of rows.
 */
const CAD_STATUS_DISPLAY: Record<
  CadListStatus,
  { label: string; color: 'success' | 'info' | 'error' | 'warning'; icon: React.ReactElement; filled?: boolean; hint: string }
> = {
  Attached: { label: 'Attached', color: 'success', icon: <CheckCircleOutline />, hint: 'A CAD report is attached.' },
  Reading: { label: 'Reading', color: 'info', icon: <HourglassEmpty />, hint: 'The attached CAD report is being read.' },
  Unreadable: {
    label: 'Unreadable',
    color: 'error',
    icon: <ErrorOutline />,
    hint: 'The attached CAD report could not be read.',
  },
  Missing: {
    label: 'Missing',
    color: 'warning',
    icon: <WarningAmber />,
    filled: true,
    hint: 'No CAD report is attached to this call.',
  },
};

function getScoreColor(score?: number): string {
  if (score == null) return 'text.secondary';
  if (score >= 70) return 'success.main';
  if (score >= 40) return 'warning.main';
  return 'error.main';
}

function renderBooleanChip(value: boolean): React.ReactNode {
  return (
    <Chip
      label={value ? 'Yes' : 'No'}
      size="small"
      color={value ? 'warning' : 'default'}
      variant="outlined"
      sx={{ height: 20, fontSize: '0.65rem' }}
    />
  );
}

function renderDateCell(value: string | null | undefined): React.ReactNode {
  return value ? (
    <Typography variant="body2" fontSize="0.75rem">
      {new Date(value).toLocaleDateString('en-US', DATE_FORMAT)}
    </Typography>
  ) : (
    <Typography variant="body2" color="text.secondary" fontSize="0.75rem">-</Typography>
  );
}

function renderDateTimeCell(value: string | null | undefined): React.ReactNode {
  if (!value) return <Typography variant="body2" fontSize="0.75rem">-</Typography>;
  const date = new Date(value);
  return (
    <Typography variant="body2" fontSize="0.75rem">
      {date.toLocaleDateString('en-US', DATE_FORMAT)}{' '}
      {date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
    </Typography>
  );
}

function getDefaultColumnConfig(): ColumnConfig[] {
  return [
    { key: 'incidentNumber', label: 'Incident ID', visible: true, sortable: true, width: '10%' },
    { key: 'audioFileName', label: 'Audio File Name', visible: true, sortable: true, width: '15%' },
    { key: 'operator', label: 'Operator Name', visible: true, sortable: true, width: '10%' },
    { key: 'callType', label: 'Call Type', visible: true, sortable: true, width: '8%' },
    { key: 'caseType', label: 'Case Type', visible: true, sortable: true, width: '10%' },
    { key: 'fileDuration', label: 'Duration', visible: true, sortable: true, width: '7%' },
    { key: 'createdDate', label: 'Processed Date', visible: true, sortable: true, width: '10%' },
    { key: 'incidentDate', label: 'Incident Date', visible: true, sortable: true, width: '10%' },
    { key: 'status', label: 'Status', visible: true, sortable: true, width: '8%' },
    { key: 'reviewStatus', label: 'Review Status', visible: true, sortable: true, width: '8%' },
    // Shown only while the agency has CAD records on: see cadEnabled below.
    { key: 'cadStatus', label: 'CAD', visible: true, sortable: false, width: '8%' },
    { key: 'callDate', label: 'Call Date (Legacy)', visible: false, sortable: true },
    { key: 'processingTime', label: 'Processing Time', visible: false, sortable: true },
    { key: 'callerName', label: 'Caller Name', visible: false, sortable: true, piiRestricted: true },
    { key: 'callerNumber', label: 'Caller Number', visible: false, sortable: true, piiRestricted: true },
    { key: 'location', label: 'Location', visible: false, sortable: true, piiRestricted: true },
    { key: 'summary', label: 'Summary', visible: false, sortable: false },
    { key: 'reviewRequested', label: 'Review Requested', visible: false, sortable: true },
    { key: 'reviewNotes', label: 'Review Notes', visible: false, sortable: false },
    { key: 'lastReviewDate', label: 'Last Review Date', visible: false, sortable: true },
    { key: 'lastReviewedBy', label: 'Last Reviewed By', visible: false, sortable: true },
    { key: 'manualScoreOverrideDate', label: 'Manual Override Date', visible: false, sortable: true },
    { key: 'manualScoreOverrideBy', label: 'Manual Override By', visible: false, sortable: true },
    { key: 'createdBy', label: 'Created By', visible: false, sortable: true },
    { key: 'modifiedDate', label: 'Modified Date', visible: false, sortable: true },
    { key: 'modifiedBy', label: 'Modified By', visible: false, sortable: true },
    { key: 'hasManualScoreOverrides', label: 'Has Manual Overrides', visible: false, sortable: true },
    // Score column always last
    { key: 'overallScore', label: 'Score', visible: true, sortable: true, width: '6%' },
  ];
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const formatters: Record<string, (value: any, call: Call) => React.ReactNode> = {
  incidentNumber: (value, call) => (
    <Typography variant="body2" fontWeight="medium" fontSize="0.75rem">
      {value || call.callId.substring(0, 8)}
    </Typography>
  ),
  audioFileName: (value) => (
    <Typography variant="body2" fontSize="0.75rem" title={value}>
      {value || '-'}
    </Typography>
  ),
  createdDate: (value) => renderDateTimeCell(value),
  incidentDate: (value, call) => {
    // Use incidentDate if available, otherwise fallback to createdDate
    return renderDateCell(value || call.createdDate);
  },
  analysisStatus: (value) => {
    if (!value) {
      return (
        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
          -
        </Typography>
      );
    }
    return (
      <Chip
        label={getStatusLabel(value)}
        size="small"
        color={getStatusColor(value)}
        sx={CHIP_SX}
      />
    );
  },
  fileDuration: (value) => (
    <Typography variant="body2" fontSize="0.75rem">
      {formatIsoDuration(value)}
    </Typography>
  ),
  callType: (value) => (
    <Chip
      label={value}
      size="small"
      variant="outlined"
      sx={CHIP_SX}
    />
  ),
  status: (value) => {
    if (!value) {
      return (
        <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
          -
        </Typography>
      );
    }
    return (
      <Chip
        label={getStatusLabel(value)}
        size="small"
        color={getStatusColor(value)}
        sx={CHIP_SX}
      />
    );
  },
  processingTime: (value, call) => (
    <Typography variant="body2" fontSize="0.75rem">
      {call.status?.toLowerCase() === 'failed' ? '-' : formatDuration(value)}
    </Typography>
  ),
  callDate: (value) => renderDateTimeCell(value),
  overallScore: (value, call) =>
    value ? (
      <Box display="flex" alignItems="center" gap={0.5}>
        <Avatar
          sx={{
            width: 24,
            height: 24,
            bgcolor: getScoreColor(value),
            fontSize: '0.65rem',
            fontWeight: 'bold',
          }}
        >
          {Math.round(value)}
        </Avatar>
        {call.hasManualScoreOverrides && (
          <Tooltip title="Scores recalculated using manual score entries">
            <Typography
              variant="caption"
              sx={{
                color: 'warning.main',
                fontWeight: 'bold',
                fontSize: '0.75rem',
              }}
            >
              *
            </Typography>
          </Tooltip>
        )}
      </Box>
    ) : (
      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
        -
      </Typography>
    ),
  cadStatus: (value: CadListStatus | undefined) => {
    const display = value ? CAD_STATUS_DISPLAY[value] : undefined;
    if (!display) {
      return <Typography variant="body2" color="text.secondary" fontSize="0.75rem">-</Typography>;
    }
    return (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
        <Chip
          icon={display.icon}
          label={display.label}
          size="small"
          color={display.color}
          variant={display.filled ? 'filled' : 'outlined'}
          sx={{
            ...CHIP_SX,
            fontSize: '0.75rem',
            '& .MuiChip-icon': { fontSize: 14, ml: 0.5 },
            // The default warning fill is white on #C2710C, under 4.5:1. The dark shade clears it.
            ...(display.filled && { bgcolor: `${display.color}.dark`, color: `${display.color}.contrastText` }),
          }}
        />
        {/* Read by a screen reader as part of the cell; a title attribute reaches only a mouse. */}
        <Box component="span" sx={visuallyHidden}>
          . {display.hint}
        </Box>
      </Box>
    );
  },
  matchScore: (value: number | undefined) => {
    if (value === undefined || value === null) {
      return <Typography variant="body2" color="text.secondary" fontSize="0.75rem">-</Typography>;
    }
    const pct = Math.round(value * 100);
    const color: 'success' | 'warning' | 'default' = value >= 0.85 ? 'success' : value >= 0.70 ? 'warning' : 'default';
    return (
      <Chip
        label={`${pct}%`}
        size="small"
        color={color}
        variant="outlined"
        sx={CHIP_SX}
      />
    );
  },
  reviewStatus: (value) =>
    value ? (
      <Chip
        label={value}
        size="small"
        color={value === FOR_REVIEW ? 'warning' : 'success'}
        variant={value === FOR_REVIEW ? 'filled' : 'outlined'}
        sx={{
          ...CHIP_SX,
          fontWeight: value === FOR_REVIEW ? 'bold' : 'normal',
        }}
      />
    ) : (
      <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
        -
      </Typography>
    ),
  location: (value) => (
    <Typography variant="body2" fontSize="0.75rem" title={value}>
      {value || '-'}
    </Typography>
  ),
  summary: (value) => (
    <Typography
      variant="body2"
      fontSize="0.75rem"
      title={value}
      sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {value || '-'}
    </Typography>
  ),
  reviewRequested: (value) => renderBooleanChip(value),
  reviewNotes: (value) => (
    <Typography
      variant="body2"
      fontSize="0.75rem"
      title={value}
      sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
    >
      {value || '-'}
    </Typography>
  ),
  lastReviewDate: (value) => renderDateCell(value),
  manualScoreOverrideDate: (value) => renderDateCell(value),
  modifiedDate: (value) => renderDateCell(value),
  hasManualScoreOverrides: (value) => renderBooleanChip(value),
};

// ---

interface SortableColumnItemProps {
  column: ColumnConfig;
  onToggle: (key: string) => void;
}

const SortableColumnItem: React.FC<SortableColumnItemProps> = ({ column, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: column.key,
    disabled: column.key === 'overallScore', // Disable dragging for score column
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isScoreColumn = column.key === 'overallScore';

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      sx={{
        bgcolor: isDragging ? 'action.hover' : isScoreColumn ? 'grey.50' : 'transparent',
        border: isDragging ? '1px dashed' : isScoreColumn ? '1px solid' : 'none',
        borderColor: isDragging ? 'primary.main' : isScoreColumn ? 'grey.300' : 'transparent',
        borderRadius: 1,
        mb: 0.5,
        cursor: isDragging ? 'grabbing' : isScoreColumn ? 'default' : 'grab',
      }}
    >
      <ListItemIcon {...(isScoreColumn ? {} : { ...attributes, ...listeners })}>
        <DragIndicator color={isScoreColumn ? 'disabled' : 'inherit'} />
      </ListItemIcon>
      <ListItemText
        primary={column.label}
        secondary={isScoreColumn ? 'Always shown last' : undefined}
      />
      <ListItemSecondaryAction>
        <Checkbox checked={column.visible} onChange={() => onToggle(column.key)} />
      </ListItemSecondaryAction>
    </ListItem>
  );
};

export const EnhancedCallsDataTable: React.FC<EnhancedCallsDataTableProps> = ({
  data,
  loading,
  searchRequest,
  onSearchRequestChange,
  onExport,
  userRole,
  onMassUpdateComplete,
  semanticMode = false,
}) => {
  const navigate = useNavigate();
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [massMarkAudited, setMassMarkAudited] = useState(false);
  const [massScore, setMassScore] = useState('');
  const [massComment, setMassComment] = useState('');
  const [massUpdating, setMassUpdating] = useState(false);
  const [massError, setMassError] = useState('');
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const [massDeleting, setMassDeleting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // The agency's minimum retention period. `null` means "not known yet, or the request failed" —
  // in which case nothing is restricted locally and the API refuses as it did before, rather than
  // the UI inventing a floor it could not read.
  const [minRetentionDays, setMinRetentionDays] = useState<number | null>(null);

  // The clock is read once, beside the policy, and never during render — reading it in a useMemo is
  // a react-hooks/purity error and an unstable render. Precision does not matter here: the boundary
  // it feeds is measured in days, so a session left open long enough to drift across one leaves the
  // button disabled a little too long, and the server still decides.
  const [policyReadAtMs, setPolicyReadAtMs] = useState<number | null>(null);

  const [pageJumpValue, setPageJumpValue] = useState('');

  const canMassUpdate = userRole === 'Supervisor' || userRole === 'TenantAdmin';
  const canViewPII = userRole === 'Supervisor' || userRole === 'TenantAdmin';

  const availableColumns = useMemo(() => {
    if (!data?.items?.length) return [];
    return Object.keys(data.items[0]);
  }, [data]);

  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>(getDefaultColumnConfig);
  const columnConfigInitialized = useRef(false);

  // Initialize columns from data only once — on first non-empty load.
  // Subsequent data reloads must not overwrite user-customized column preferences.
  React.useEffect(() => {
    if (availableColumns.length > 0 && !columnConfigInitialized.current) {
      columnConfigInitialized.current = true;
      const defaultConfig = getDefaultColumnConfig();
      const defaultKeys = defaultConfig.map((col) => col.key);

      const additionalColumns = availableColumns
        .filter((key) => !defaultKeys.includes(key))
        .map((key) => ({
          key,
          label: formatColumnLabel(key),
          visible: false,
          sortable: true,
        }));

      // Ensure overallScore is always last
      const allColumns = [
        ...defaultConfig.filter((col) => col.key !== 'overallScore'),
        ...additionalColumns,
        ...defaultConfig.filter((col) => col.key === 'overallScore'),
      ];

      setColumnConfig(allColumns);
    }
  }, [availableColumns]);

  // The API leaves cadStatus off every row while the agency has CAD records off, so an agency that
  // never uses them is not shown a column of dashes.
  const cadEnabled = useMemo(() => !!data?.items?.some((call) => call.cadStatus), [data]);

  const visibleColumns = useMemo(() => {
    const base = columnConfig.filter(
      (col) => col.visible && (!col.piiRestricted || canViewPII) && (col.key !== 'cadStatus' || cadEnabled)
    );
    return semanticMode ? [matchScoreColumn, ...base] : base;
  }, [columnConfig, canViewPII, semanticMode, cadEnabled]);

  const dialogColumns = useMemo(
    () => columnConfig.filter((col) => (!col.piiRestricted || canViewPII) && (col.key !== 'cadStatus' || cadEnabled)),
    [columnConfig, canViewPII, cadEnabled]
  );

  const handleSort = (property: string) => {
    const isCurrentSort = searchRequest.sortBy === property;
    const newSortDescending = isCurrentSort ? !searchRequest.sortDescending : false;

    onSearchRequestChange({
      ...searchRequest,
      sortBy: property,
      sortDescending: newSortDescending,
      page: 1,
    });
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    onSearchRequestChange({
      ...searchRequest,
      page: newPage + 1,
    });
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onSearchRequestChange({
      ...searchRequest,
      pageSize: parseInt(event.target.value, 10),
      page: 1,
    });
  };

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExport = (format: 'csv' | 'excel') => {
    onExport({
      format,
      visibleColumns: visibleColumns.map((col) => ({ key: col.key, label: col.label })),
      currentData: data?.items || [],
    });
    handleExportClose();
  };

  const handleViewCall = (call: Call) => {
    // Don't navigate for failed calls since there's no analysis data
    if (call.status?.toLowerCase() === 'failed') {
      return;
    }
    navigate(`/call/${call.callId}`);
  };

  const selectableItems = useMemo(
    () => (data?.items || []).filter((c) => c.status?.toLowerCase() !== 'failed'),
    [data]
  );

  const allSelectableSelected = useMemo(
    () => selectableItems.length > 0 && selectableItems.every((c) => selectedIds.has(c.callId)),
    [selectableItems, selectedIds]
  );

  const someSelected = selectedIds.size > 0;

  const policyFetchedAtRef = useRef(0);

  // A promise chain rather than async/await, and the state is set inside `.then` rather than after
  // an await: react-hooks/set-state-in-effect flags a synchronous path from an effect to a setter,
  // and the awaited form gives it one.
  const loadRetentionPolicy = useCallback(() => {
    if (!canMassUpdate) return;

    // Stamped at the START, so the throttle counts attempts rather than successes. Stamping only in
    // `.then` leaves the ref at 0 while the endpoint is failing, which makes the five-minute gate
    // permanently open: every checkbox click fires a fresh GET, each amplified to four requests by
    // the axios retry policy, and the deliberately silent `.catch` guarantees nobody sees it.
    policyFetchedAtRef.current = Date.now();

    CallService.getRetentionPolicy()
      .then((days) => {
        setMinRetentionDays(days);
        setPolicyReadAtMs(Date.now());
      })
      .catch(() => {
        // Deliberately silent. An unreadable retention policy is not something a supervisor looking
        // at a call list can act on, and the deletion path stays correct without it — nothing is
        // restricted locally and the API refuses exactly as it did before. That is only acceptable
        // because the API's refusal is now actually readable; see the outcome Alert, which renders
        // outside the collapsing action bar.
      });
  }, [canMassUpdate]);

  useEffect(() => {
    loadRetentionPolicy();
  }, [loadRetentionPolicy]);

  /**
   * Refreshes the retention context when the selection changes.
   *
   * Selection change is the right hook and the confirm dialog is the wrong one, which is not
   * obvious: the harmful direction of staleness is a call that ages PAST the floor while the tab
   * sits open — the button stays disabled, so the dialog cannot be opened, so a refresh tied to
   * opening it never fires in the one case that needs it. A dispatch console keeps a tab open for a
   * whole shift, and on a quiet night the list can go hours without changing, so the list load is
   * not a sufficient hook either.
   *
   * Stamping the clock is free and fixes the drift that moves continuously. The policy itself is
   * re-read only when the last read is stale, so ticking fifty checkboxes does not fire fifty GETs.
   * Everything fails closed meanwhile, so this is legibility, not safety: the difference between a
   * briefly conservative button and one stuck until a reload the user has no reason to suspect.
   */
  const refreshRetentionContext = useCallback(() => {
    setPolicyReadAtMs(Date.now());
    if (Date.now() - policyFetchedAtRef.current > POLICY_MAX_AGE_MS) {
      loadRetentionPolicy();
    }
  }, [loadRetentionPolicy]);

  /**
   * The instant a call becomes deletable, or null when no floor applies.
   *
   * `createdDate` is a UTC instant and this returns one; it is rendered in the reader's own zone at
   * the point of display. Never format the instant as a UTC calendar date and call it a day — an
   * agency west of UTC would be shown tomorrow's date (lessons.md 2026-07-30).
   */
  const deletableAt = useCallback(
    (call: Call): Date | null => {
      if (!minRetentionDays || minRetentionDays <= 0) return null;
      const created = new Date(call.createdDate);
      return new Date(created.getTime() + minRetentionDays * 86_400_000);
    },
    [minRetentionDays]
  );

  /**
   * Selected calls on this page that the retention floor still protects.
   *
   * Only the current page can be judged — a selection can span pages — so the counts below say "at
   * least" whenever some selected call is not visible. The server enforces the floor regardless;
   * this exists so the button stops offering what will be refused, not as the control itself.
   */
  const protectedSelected = useMemo(() => {
    if (policyReadAtMs === null) return [];
    return (data?.items || []).filter((c) => {
      if (!selectedIds.has(c.callId)) return false;
      const at = deletableAt(c);
      return at !== null && at.getTime() > policyReadAtMs;
    });
  }, [data, selectedIds, deletableAt, policyReadAtMs]);

  // Only when every selected call is accounted for AND every one of them is protected: with a
  // selection spanning pages the counts cannot match, so the button stays enabled and the server
  // has the final say.
  const allSelectedAreProtected =
    someSelected && protectedSelected.length === selectedIds.size;

  // What a confirm would actually delete, when that is knowable. The auto-refresh can change `data`
  // while the dialog is open, so a call can cross the retention boundary underneath it — without
  // this the dialog could offer "delete 0 of 5" with an armed confirm button.
  const deletableSelectedCount = selectedIds.size - protectedSelected.length;

  const earliestDeletable = useMemo(() => {
    const dates = protectedSelected
      .map((c) => deletableAt(c))
      .filter((d): d is Date => d !== null)
      .sort((a, b) => a.getTime() - b.getTime());
    return dates.length > 0 ? dates[0] : null;
  }, [protectedSelected, deletableAt]);

  /**
   * Selected calls that are not on this page, so nothing local can be said about them.
   *
   * Selection survives a page change and there is no UI for reviewing it across pages, so the
   * counts below can only describe what is visible. Naming the remainder is the difference between
   * "none of your selection is protected" and "we cannot see the rest" — the second is the truth,
   * and the first is what a bare count of zero would have implied.
   */
  const offPageSelectedCount = useMemo(() => {
    const visible = (data?.items || []).filter((c) => selectedIds.has(c.callId)).length;
    return selectedIds.size - visible;
  }, [data, selectedIds]);

  const retentionNotice = useMemo(() => {
    const parts: string[] = [];

    if (protectedSelected.length > 0) {
      const all = protectedSelected.length === selectedIds.size;
      const noun = protectedSelected.length === 1 ? 'call' : 'calls';
      const subject = all
        ? `All ${protectedSelected.length} selected ${noun}`
        : offPageSelectedCount > 0
          ? `At least ${protectedSelected.length} of the selected ${noun}`
          : `${protectedSelected.length} of the selected ${noun}`;
      const verb = protectedSelected.length === 1 ? 'is' : 'are';

      // The instant, not the calendar date. A bare date reads as "deletable from midnight", but the
      // boundary lands at the call's own time of day — so a supervisor would try on the morning of
      // the stated day and be refused. Rendering the time is the honest form; rounding DOWN to a
      // date is the one direction that produces a refusal.
      const earliest = earliestDeletable
        ? ` The earliest can be deleted from ${earliestDeletable.toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}.`
        : '';

      parts.push(
        `${subject} ${verb} inside this agency's ${minRetentionDays}-day minimum retention ` +
        `period and cannot be deleted yet.${earliest}`
      );
    }

    if (offPageSelectedCount > 0) {
      parts.push(
        `${offPageSelectedCount} selected ${offPageSelectedCount === 1 ? 'call is' : 'calls are'} ` +
        `not on this page; any inside the retention period will not be deleted.`
      );
    }

    return parts.join(' ');
  }, [
    protectedSelected,
    selectedIds,
    offPageSelectedCount,
    earliestDeletable,
    minRetentionDays,
  ]);

  const handleSelectAll = () => {
    refreshRetentionContext();
    if (allSelectableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableItems.map((c) => c.callId)));
    }
  };

  const handleSelectRow = (callId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    refreshRetentionContext();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(callId)) {
        next.delete(callId);
      } else {
        next.add(callId);
      }
      return next;
    });
  };

  const handleClearSelection = () => {
    setSelectedIds(new Set());
    setMassMarkAudited(false);
    setMassScore('');
    setMassComment('');
    setMassError('');
  };

  const totalPages = data ? Math.ceil(data.totalCount / searchRequest.pageSize) : 0;

  const handlePageJump = () => {
    const page = parseInt(pageJumpValue, 10);
    if (page >= 1 && page <= totalPages) {
      onSearchRequestChange({ ...searchRequest, page });
      setPageJumpValue('');
    }
  };

  const handleMassUpdateSubmit = async () => {
    setConfirmDialogOpen(false);
    setMassUpdating(true);
    setMassError('');

    const scoreVal = massScore.trim() ? parseFloat(massScore) : undefined;
    if (scoreVal !== undefined && (isNaN(scoreVal) || scoreVal < 0 || scoreVal > 100)) {
      setMassError('Score must be a number between 0 and 100');
      setMassUpdating(false);
      return;
    }

    try {
      const request: MassUpdateRequest = {
        callIds: Array.from(selectedIds),
        markAsAudited: massMarkAudited,
        overrideScore: scoreVal,
        comment: massComment.trim() || undefined,
      };
      await CallService.massUpdateCalls(request);
      handleClearSelection();
      onMassUpdateComplete?.();
    } catch {
      setMassError('Mass update failed. Please try again.');
    } finally {
      setMassUpdating(false);
    }
  };

  const massUpdateEnabled = massMarkAudited || massScore.trim() !== '';

  const handleMassDeleteSubmit = async () => {
    setDeleteConfirmOpen(false);
    setMassDeleting(true);
    setMassError('');
    try {
      const request: MassDeleteRequest = { callIds: Array.from(selectedIds) };
      const result = await CallService.massDeleteCalls(request);

      // Clear the selection FIRST. handleClearSelection resets massError, so setting the message
      // before it wiped the message every time — the per-call refusals the API returns (a call
      // inside the retention floor is one) were computed, sent, and then discarded unread, which
      // is why a blocked delete looked like a silent no-op rather than a refusal. The message now
      // also renders outside the collapsing action bar, which is the other half of that fix.
      handleClearSelection();

      if (result.errors.length > 0) {
        // Summarised rather than echoing result.errors[0] verbatim: that is a developer string
        // carrying a call GUID, and it is only the first of N. The retention rule is stated as a
        // rule rather than attributed to every failure, because a call deleted by someone else in
        // the meantime lands in the same list.
        const why = minRetentionDays
          ? ` Calls inside the agency's ${minRetentionDays}-day minimum retention period cannot be deleted yet.`
          : '';
        setMassError(
          `${result.deletedCount} deleted, ${result.errors.length} not deleted.${why}`
        );
      }
      onMassUpdateComplete?.();
    } catch {
      handleClearSelection();
      setMassError('Delete failed. Please try again.');
    } finally {
      setMassDeleting(false);
    }
  };

  const handleColumnToggle = (columnKey: string) => {
    setColumnConfig((prev) =>
      prev.map((col) => (col.key === columnKey ? { ...col, visible: !col.visible } : col))
    );
  };

  const handleResetToDefaults = () => {
    const defaultConfig = getDefaultColumnConfig();
    const currentAdditionalColumns = columnConfig.filter(
      (col) => !defaultConfig.some((defaultCol) => defaultCol.key === col.key)
    );

    // Reset to defaults plus keep additional columns but make them invisible
    const resetConfig = [
      ...defaultConfig.filter((col) => col.key !== 'overallScore'),
      ...currentAdditionalColumns.map((col) => ({ ...col, visible: false })),
      ...defaultConfig.filter((col) => col.key === 'overallScore'),
    ];

    setColumnConfig(resetConfig);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleColumnReorder = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      setColumnConfig((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over?.id);

        // Don't allow reordering overallScore - it should always be last
        if (active.id === 'overallScore' || over?.id === 'overallScore') {
          return items;
        }

        const reordered = arrayMove(items, oldIndex, newIndex);

        // Ensure overallScore is always at the end
        const scoreIndex = reordered.findIndex((item) => item.key === 'overallScore');
        if (scoreIndex !== -1 && scoreIndex !== reordered.length - 1) {
          const scoreItem = reordered.splice(scoreIndex, 1)[0];
          reordered.push(scoreItem);
        }

        return reordered;
      });
    }
  };

  /*
    Rendered in EVERY branch below, including the two early returns, and that is the whole point.
    A mass delete calls onMassUpdateComplete, which sets `loading` synchronously — so the render
    that carries the outcome message is the same one that returns the loading placeholder, and if
    the refreshed page comes back empty it returns "No calls found" instead. The message then never
    renders at all, which is reachable on the error that matters most: a call deleted whose audio
    could NOT be removed from storage. Deleting the last call on a filtered page hits exactly that
    path. Found by design-reviewer as BLOCKING after the first fix moved this above the Collapse but
    left it below the guards — the same mistake at two scales.

    The live region is unconditional rather than mounted with its text: a region inserted into the
    tree already populated is routinely not announced, and the first protected selection is exactly
    when it would have been mounted. It exists from first render and only its text changes. An empty
    block generates no line box, so it costs nothing when idle — do not switch it to `display: none`
    to "tidy" that, which removes it from the tree and reinstates the defect.
  */
  const outcomeBanner = canMassUpdate && massError ? (
    <Alert severity="error" onClose={() => setMassError('')} sx={{ mx: 2, mt: 1 }}>
      {massError}
    </Alert>
  ) : null;

  const retentionLiveRegion = canMassUpdate ? (
    <Typography
      role="status"
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block', mx: 2, mb: retentionNotice ? 1 : 0 }}
    >
      {retentionNotice}
    </Typography>
  ) : null;

  if (loading) {
    return (
      <Paper sx={{ width: '100%', mb: 2 }}>
        {outcomeBanner}
        {retentionLiveRegion}
        <LinearProgress />
        <Box p={3}>
          <Typography>Loading calls...</Typography>
        </Box>
      </Paper>
    );
  }

  if (!data || data.items.length === 0) {
    return (
      <Paper sx={{ width: '100%', mb: 2 }}>
        {outcomeBanner}
        {retentionLiveRegion}
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No calls found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria or filters
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      {/*
        FIRST TWO CHILDREN in every branch, matching the two early returns above. React reconciles
        unkeyed children by index, so putting these after the header here and before it there would
        remount the live region on every loading flip — losing the "already present in the tree"
        property exactly at a page change, which is when `offPageSelectedCount` changes its text.
        That is the same non-announcement this was moved out of the Collapse to fix.
      */}
      {outcomeBanner}
      {retentionLiveRegion}

      <Box display="flex" justifyContent="space-between" alignItems="center" p={1.5}>
        <Typography variant="h6" fontSize="1.1rem">
          Calls ({data.totalCount.toLocaleString()})
        </Typography>
        <Box display="flex" gap={1}>
          <Tooltip title="Manage columns">
            <IconButton onClick={() => setColumnDialogOpen(true)} size="small">
              <ViewColumn />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export data">
            <IconButton onClick={handleExportClick} size="small">
              <GetApp />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Top Pagination Bar */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 0.75, borderTop: '1px solid', borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
            Rows:
          </Typography>
          <select
            value={searchRequest.pageSize}
            onChange={(e) =>
              onSearchRequestChange({ ...searchRequest, pageSize: parseInt(e.target.value, 10), page: 1 })
            }
            style={{ fontSize: '0.8rem', padding: '2px 4px', borderRadius: 4, border: '1px solid #ccc' }}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
        </Box>

        <Box display="flex" alignItems="center" gap={0.5}>
          <Tooltip title="First page">
            <span>
              <IconButton
                size="small"
                disabled={searchRequest.page <= 1}
                onClick={() => onSearchRequestChange({ ...searchRequest, page: 1 })}
              >
                <FirstPage fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Previous page">
            <span>
              <IconButton
                size="small"
                disabled={searchRequest.page <= 1}
                onClick={() => onSearchRequestChange({ ...searchRequest, page: searchRequest.page - 1 })}
              >
                <NavigateBefore fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Typography variant="body2" fontSize="0.8rem" sx={{ mx: 0.5 }}>
            Page
          </Typography>
          <TextField
            size="small"
            value={pageJumpValue}
            onChange={(e) => setPageJumpValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handlePageJump(); }}
            placeholder={String(searchRequest.page)}
            sx={{ width: 52, '& .MuiInputBase-input': { py: 0.25, px: 0.5, fontSize: '0.8rem', textAlign: 'center' } }}
          />
          <Typography variant="body2" fontSize="0.8rem" sx={{ mx: 0.5 }}>
            of {totalPages}
          </Typography>

          <Tooltip title="Next page">
            <span>
              <IconButton
                size="small"
                disabled={searchRequest.page >= totalPages}
                onClick={() => onSearchRequestChange({ ...searchRequest, page: searchRequest.page + 1 })}
              >
                <NavigateNext fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Last page">
            <span>
              <IconButton
                size="small"
                disabled={searchRequest.page >= totalPages}
                onClick={() => onSearchRequestChange({ ...searchRequest, page: totalPages })}
              >
                <LastPage fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Mass Action Bar — visible when rows are selected */}
      {canMassUpdate && (
        <Collapse in={someSelected}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', borderBottom: '1px solid', borderColor: 'primary.light' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="body2" fontWeight="bold" color="primary.main">
                {selectedIds.size} call{selectedIds.size !== 1 ? 's' : ''} selected
              </Typography>
              <IconButton size="small" onClick={handleClearSelection}>
                <Close fontSize="small" />
              </IconButton>
            </Box>


            <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap" useFlexGap>
              <Box display="flex" alignItems="center" gap={0.5}>
                <Checkbox
                  size="small"
                  checked={massMarkAudited}
                  onChange={(e) => setMassMarkAudited(e.target.checked)}
                />
                <Typography variant="body2" fontSize="0.8rem">Mark as Audited</Typography>
              </Box>

              <TextField
                size="small"
                label="Override Score (0-100)"
                type="number"
                value={massScore}
                onChange={(e) => setMassScore(e.target.value)}
                inputProps={{ min: 0, max: 100, step: 1 }}
                sx={{ width: 160, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' } }}
              />

              <TextField
                size="small"
                label="Comment (optional)"
                value={massComment}
                onChange={(e) => setMassComment(e.target.value)}
                placeholder="Reason for mass update..."
                sx={{ minWidth: 220, flex: 1, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' } }}
              />

              <Button
                variant="contained"
                size="small"
                disabled={!massUpdateEnabled || massUpdating}
                onClick={() => setConfirmDialogOpen(true)}
                startIcon={massUpdating ? <CircularProgress size={14} /> : <CheckCircle />}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {massUpdating ? 'Updating...' : 'Apply Mass Update'}
              </Button>

              {/*
                Disabled only when every selected call is protected — a mixed selection still
                deletes what it can, and the dialog says what it will leave behind. The reason is
                carried by the visible status line above, which is what a keyboard or screen-reader
                user gets; this tooltip is redundant reinforcement for a pointer user, not the only
                channel. `span` because MUI cannot attach a tooltip to a disabled button.
              */}
              <Tooltip title={allSelectedAreProtected ? retentionNotice : ''}>
                <span style={{ display: 'inline-flex' }}>
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={massDeleting || massUpdating || allSelectedAreProtected}
                    onClick={() => {
                      // An addition to the selection-change hook, not a replacement for it: a user
                      // who selects at 09:00 and deletes at 18:00 would otherwise see counts and a
                      // deletable-from time computed nine hours earlier.
                      refreshRetentionContext();
                      setDeleteConfirmOpen(true);
                    }}
                    startIcon={massDeleting ? <CircularProgress size={14} /> : <DeleteOutlined />}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {massDeleting ? 'Deleting...' : 'Delete Selected'}
                  </Button>
                </span>
              </Tooltip>
            </Stack>
          </Box>
        </Collapse>
      )}

      <TableContainer>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'auto',
            '& .MuiTableCell-root': { py: 0.5, fontSize: '0.8rem' },
          }}
        >
          <TableHead>
            <TableRow>
              {canMassUpdate && (
                <TableCell padding="checkbox" sx={{ width: 42 }}>
                  <Checkbox
                    size="small"
                    indeterminate={someSelected && !allSelectableSelected}
                    checked={allSelectableSelected}
                    onChange={handleSelectAll}
                  />
                </TableCell>
              )}
              {visibleColumns.map((column) => (
                <TableCell key={column.key} sx={{ width: column.width, whiteSpace: 'nowrap' }}>
                  {/* Semantic results are ranked by similarity — column sort does not apply, so
                      render plain labels (an active sort arrow here would contradict the order). */}
                  {column.sortable && !semanticMode ? (
                    <TableSortLabel
                      active={searchRequest.sortBy === column.key}
                      direction={searchRequest.sortDescending ? 'desc' : 'asc'}
                      onClick={() => handleSort(column.key)}
                    >
                      {column.label}
                    </TableSortLabel>
                  ) : (
                    column.label
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.items.map((call) => {
              const isFailed = call.status?.toLowerCase() === 'failed';
              const isSelected = selectedIds.has(call.callId);
              return (
                <TableRow
                  key={call.callId}
                  hover={!isFailed}
                  selected={isSelected}
                  onClick={() => handleViewCall(call)}
                  sx={{
                    '&:last-child td': {
                      borderBottom: '2px solid',
                      borderBottomColor: 'divider',
                    },
                    height: 40,
                    cursor: isFailed ? 'default' : 'pointer',
                    '&:hover': {
                      backgroundColor: isFailed ? 'transparent' : 'action.hover',
                    },
                    opacity: isFailed ? 0.6 : 1,
                  }}
                >
                  {canMassUpdate && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        disabled={isFailed}
                        onClick={(e) => handleSelectRow(call.callId, e)}
                      />
                    </TableCell>
                  )}
                  {visibleColumns.map((column) => (
                    <TableCell key={column.key}>
                      {/* eslint-disable @typescript-eslint/no-explicit-any */}
                      {formatters[column.key]
                        ? formatters[column.key]((call as any)[column.key], call)
                        : (call as any)[column.key] || '-'}
                      {/* eslint-enable @typescript-eslint/no-explicit-any */}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[...PAGE_SIZE_OPTIONS]}
        component="div"
        count={data.totalCount}
        rowsPerPage={searchRequest.pageSize}
        page={searchRequest.page - 1}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />

      {/* Review Status Footnote */}
      <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
          * Calls with overall score below 70 are automatically flagged for review
        </Typography>
      </Box>

      {/* Column Management Dialog */}
      <Dialog
        open={columnDialogOpen}
        onClose={() => setColumnDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Manage Columns</DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Select which columns to display and drag to reorder them.
            </Typography>
            <Button variant="outlined" size="small" onClick={handleResetToDefaults} sx={{ ml: 2 }}>
              Reset to Defaults
            </Button>
          </Box>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleColumnReorder}
          >
            <SortableContext
              items={dialogColumns.map((col) => col.key)}
              strategy={verticalListSortingStrategy}
            >
              <List>
                {dialogColumns.map((column) => (
                  <SortableColumnItem
                    key={column.key}
                    column={column}
                    onToggle={handleColumnToggle}
                  />
                ))}
              </List>
            </SortableContext>
          </DndContext>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setColumnDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
      </Menu>

      {/* Mass Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        {/*
          The title names what will actually be deleted. "Permanently delete 10 calls?" over an
          alert saying 6 of them cannot be is a contradiction, and the title is the number a user
          remembers. When part of the selection is off this page the count is unknown — so the title
          drops the number entirely rather than asserting a total it cannot see, and the alert below
          carries every figure. A plain count is not an abstention; it is still a claim.
        */}
        <DialogTitle>
          {offPageSelectedCount > 0
            ? 'Permanently delete the selected calls?'
            : protectedSelected.length > 0
              ? `Permanently delete ${deletableSelectedCount} of ${selectedIds.size} selected calls?`
              : `Permanently delete ${selectedIds.size} call${selectedIds.size !== 1 ? 's' : ''}?`}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            This will permanently delete the selected call records and their audio files from storage.
            This action cannot be undone.
          </Typography>
          {/*
            Said before the confirmation, not discovered after it. The retention floor is the
            agency's own records-retention schedule — deliberately NOT described as a CJIS
            requirement, which governs audit records rather than recordings.
          */}
          {retentionNotice && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {retentionNotice}
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Each deletion is audit-logged per CJIS AU-2 requirements.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          {/*
            Disarmed when nothing in the selection can actually be deleted — reachable if the list
            refreshes under an open dialog. Confirming a destructive action that will do nothing
            teaches the user the confirmation means nothing.
          */}
          <Button
            variant="contained"
            color="error"
            disabled={offPageSelectedCount === 0 && deletableSelectedCount === 0}
            onClick={handleMassDeleteSubmit}
          >
            Delete Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mass Update Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm Mass Update</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            You are about to update {selectedIds.size} call{selectedIds.size !== 1 ? 's' : ''}:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mt: 1, '& li': { fontSize: '0.85rem', mb: 0.5 } }}>
            {massMarkAudited && <li>Mark as Audited</li>}
            {massScore.trim() && <li>Set score to {massScore}</li>}
            {massComment.trim() && <li>Add comment</li>}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            This action will be audit-logged per CJIS requirements.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleMassUpdateSubmit}>Confirm</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
